/**
 * EircodePrompt — inline "Add Eircode" form rendered at the top of the
 * Passport when the listing's `location.eircode` isn't set.
 *
 * Why inline (not in a hidden agent-edit form)?
 *   Because the agent looking at their own listing sees the county-level
 *   data right there + a one-field shortcut to unlock the precise version.
 *   The fewest-clicks path.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Check, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
const EIRCODE_RE = /^[ACDEFHKNPRTVWXY][0-9]{2}\s?[A-Z0-9]{4}$/i;

const EircodePrompt = ({ propertyId, onSaved }) => {
  const [val, setVal] = useState('');
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setErr(null);
    if (!EIRCODE_RE.test(val.trim())) {
      setErr('Format looks off — Irish Eircodes are 7 chars like A65 F4E2');
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/api/properties/${propertyId}/eircode`, {
        eircode: val.trim().toUpperCase(),
      });
      setSaved(true);
      onSaved?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div
        className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 flex items-center gap-2 text-sm text-emerald-800"
        data-testid="passport-eircode-saved"
      >
        <Check className="h-4 w-4" />
        Eircode saved — precise data is loading.
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-amber-200 bg-amber-50/50 p-3"
      data-testid="passport-eircode-prompt"
    >
      <div className="flex items-start gap-3 flex-wrap sm:flex-nowrap">
        <MapPin className="h-4 w-4 text-amber-700 flex-shrink-0 mt-1.5" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-stone-900">
            Add the Eircode to unlock precise data
          </div>
          <div className="text-xs text-stone-600">
            Without it we&apos;re showing county-level estimates. Takes 5 seconds.
          </div>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <input
            type="text"
            value={val}
            onChange={(e) => { setVal(e.target.value); setErr(null); }}
            placeholder="e.g. A65 F4E2"
            data-testid="passport-eircode-input"
            className="text-sm px-2.5 py-1.5 rounded border border-stone-300 focus:outline-none focus:border-purple-500 w-full sm:w-40 uppercase"
            disabled={saving}
            autoComplete="off"
          />
          <Button
            type="submit"
            size="sm"
            disabled={saving || !val.trim()}
            data-testid="passport-eircode-submit"
            className="bg-amber-600 hover:bg-amber-700 text-xs"
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
      {err && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-700" data-testid="passport-eircode-error">
          <AlertTriangle className="h-3.5 w-3.5" /> {err}
        </div>
      )}
    </form>
  );
};

export default EircodePrompt;
